<template>
  <div class="requirement-files">
    <div
      v-if="files.length === 0"
      class="no-files-message"
    >
      <p>No {{ formatFileType(fileType) }} files uploaded yet.</p>
    </div>
    <div
      v-else
      class="files-list"
    >
      <div
        v-for="file in files"
        :key="file.id || file.file_id"
        class="file-item"
      >
        <div
          class="file-icon-small"
          :class="{
            'pdf-file': isPdfFile(file),
            'excel-file': isExcelFile(file)
          }"
        >
          <i :class="getFileIcon(file)"></i>
        </div>
        <div class="file-details">
          <div class="file-name-small">{{ getFileName(file) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RequirementFilesList',
  props: {
    files: {
      type: Array,
      required: true,
      default: () => []
    },
    fileType: {
      type: String,
      required: true
    }
  },
  methods: {
    getFileName(file) {
      return file.name || file.file_name || 'Unknown File';
    },

    getFileExtension(file) {
      const name = this.getFileName(file);
      const parts = name.split('.');
      if (parts.length < 2) return '';
      return parts.pop().toLowerCase();
    },

    getFileIcon(file) {
      const documentType = file.document_type || file.type;
      const ext = this.getFileExtension(file);

      if (documentType) {
        switch (documentType) {
          case 'application/pdf':
          case 'pdf':
            return 'las la-file-pdf';
          case 'application/vnd.ms-excel':
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          case 'excel':
          case 'xlsx':
          case 'xls':
            return 'las la-file-excel';
          case 'application/msword':
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          case 'word':
          case 'doc':
          case 'docx':
            return 'las la-file-word';
          case 'application/vnd.ms-powerpoint':
          case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          case 'powerpoint':
          case 'ppt':
          case 'pptx':
            return 'las la-file-powerpoint';
          case 'text/csv':
          case 'csv':
            return 'las la-file-csv';
          case 'application/json':
          case 'json':
            return 'las la-file-code';
          case 'image/jpeg':
          case 'image/png':
          case 'image/gif':
          case 'image':
            return 'las la-file-image';
        }
      }

      switch (ext) {
        case 'pdf':
          return 'las la-file-pdf';
        case 'xlsx':
        case 'xls':
        case 'csv':
          return 'las la-file-excel';
        case 'doc':
        case 'docx':
          return 'las la-file-word';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'las la-file-image';
        default:
          return 'las la-file-alt';
      }
    },

    isPdfFile(file) {
      const documentType = file.document_type || file.type;
      if (documentType === 'application/pdf' || documentType === 'pdf') {
        return true;
      }
      return this.getFileExtension(file) === 'pdf';
    },

    isExcelFile(file) {
      const documentType = file.document_type || file.type;
      const ext = this.getFileExtension(file);
      if (documentType === 'application/vnd.ms-excel'
        || documentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        || documentType === 'excel') {
        return true;
      }
      return ['xlsx', 'xls', 'csv'].includes(ext);
    },

    formatFileType(type) {
      switch (type) {
        case 'bank_statement':
          return 'Bank Statement';
        case 'emirates_id':
          return 'Emirates ID';
        case 'trade_license':
        case 'trade_licence':
          return 'Trade License';
        case 'vat_statement':
          return 'VAT Statement';
        case 'aecb_report':
          return 'AECB Report';
        case 'payer_list':
          return 'Payer List';
        case 'credit_model':
          return 'Credit Model';
        default:
          return type || 'Unknown';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.requirement-files {
  padding: 0;
  background: transparent;

  .no-files-message {
    text-align: center;
    padding: 0.75rem 0.5rem;
    color: #718096;

    p {
      margin: 0;
      font-size: 0.85rem;
      font-family: $brand-font-regular;
    }
  }

  .files-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .file-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.65rem 0.75rem;
    border-radius: 8px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    transition: background 0.15s ease;

    &:hover {
      background: #f1f5f9;
    }
  }

  .file-icon-small {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 0.25rem;

    i {
      font-size: 1.25rem;
      color: $color-primary;
    }

    &.pdf-file i {
      color: #dc2626;
    }

    &.excel-file i {
      color: #028a0f;
    }
  }

  .file-details {
    flex: 1;
    min-width: 0;
    padding-top: 0.2rem;
  }

  .file-name-small {
    font-size: 0.875rem;
    font-weight: 500;
    color: #2d3748;
    margin: 0;
    font-family: $brand-font-medium;
    line-height: 1.4;
    word-break: break-word;
    white-space: normal;
  }
}
</style>
